import { Flex, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { divide, entriesIn, map, max, multiply, valuesIn } from "lodash";

import { builder } from "@/builders";
import { useFakeAdminDashboardData } from "@/builders/types/admin-dashboard";
import { EmptySlot } from "@/components/staff/shared/empty-slot";
import { useCurrentORG } from "@/components/super-admin/org-management/use-organisation";

import clsx from "clsx";
import { CoursePerformanceBar } from "./course-performance-bar";

export function CoursePerformance() {
  const initialDashboardData = useFakeAdminDashboardData();
  const { organization } = useCurrentORG();

  const { data = [], isPlaceholderData } = useQuery({
    queryKey: builder.data.admin_dashboard.$get({
      organization: organization.id,
    }),
    queryFn: () =>
      builder.$use.data.admin_dashboard({
        organization: organization.id,
      }),
    select: ({ data }) => {
      const { most_enrolled_courses } = data;
      const maxEnrollment = max(valuesIn(most_enrolled_courses)) || 1;

      return map(entriesIn(most_enrolled_courses), ([course, enrollment]) => ({
        course,
        value: enrollment,
        ratio: multiply(divide(enrollment, maxEnrollment), 100),
      }));
    },
    placeholderData: {
      data: initialDashboardData,
    },
  });

  return (
    <Stack
      px={20}
      pt={20}
      flex={1}
      className="flex-1 border rounded-lg border-primary-border-light bg-primary-background-white"
      gap={28}
    >
      <Stack gap={2}>
        <Title
          className={clsx("prose-xl/medium text-primary-text-body", {
            skeleton: isPlaceholderData,
          })}
        >
          Course Performance
        </Title>
        <Text
          className={clsx("text-primary-text-subtle prose-sm/regular", {
            skeleton: isPlaceholderData,
          })}
        >
          Top performing courses
        </Text>
      </Stack>

      {data.length ? (
        <Flex className="prose-sm/medium" gap={4} flex={1}>
          {data.map(({ course, value, ratio }, index) => (
            <CoursePerformanceBar
              skeleton={isPlaceholderData}
              key={index}
              label={course}
              value={value}
              ratio={ratio}
            />
          ))}
        </Flex>
      ) : (
        <EmptySlot
          title="No data found"
          description="Create a course to get started"
        />
      )}
    </Stack>
  );
}
